<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">
        {{ $t('ITEM.EDIT') }} {{ $t('DEVICES.ITEM') }}
      </span>

      <v-spacer />

      <v-btn icon @click="cancel">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <slot v-if="showDetails" name="details" :device="device">
        <device-details class="pa-4" :device="device" />
      </slot>

      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        :readonly="!$can('sensitive-data')"
      >
        <device-form
          v-model="device"
          :type="$options.type"
          :action="$options.action"
          :errors="errors.getAll()"
          @update-prop="clearErrorByName($event)"
        />
      </v-form>
    </v-card-text>

    <v-card-actions v-if="$can('sensitive-data')">
      <v-btn outlined depressed color="grey" @click="cancel">
        {{ $t('BUTTON.CANCEL') }}
      </v-btn>

      <v-btn depressed color="error" @click="deleteDevice">{{
        $t('BUTTON.DELETE')
      }}</v-btn>

      <v-spacer />

      <v-btn depressed color="primary" :disabled="!valid" @click="update">
        {{ $t('BUTTON.UPDATE') }}
      </v-btn>
    </v-card-actions>
    <confirm-dialog ref="confirm"></confirm-dialog>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import errors from '@/modules/_global/mixins/errors';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import { EDIT } from '@/modules/_global/mixins/form-editable';
import { dateFormat } from '@/modules/_global/filters';
import { onceRequest } from '@/modules/_global/utils/common';
import { WarehouseDevicesService } from '@/services/warehouse-devices.service';
import DeviceForm, {
  WAREHOUSE
} from '@/modules/devices/components/forms/DeviceForm.vue';
import { cloneDeep } from 'lodash';
import checkDeviceCode from '@/modules/devices/mixins/checkDeviceCode';

export default {
  name: 'EditDevice',

  action: EDIT,

  type: WAREHOUSE,

  filters: {
    dateFormat
  },

  components: {
    DeviceForm,
    DeviceDetails: () => import('./DeviceDetails.vue'),
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog')
  },

  mixins: [errors, i18nRefreshData, checkDeviceCode],

  props: {
    id: {
      type: Number,
      required: true
    },
    showDetails: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      valid: true,
      device: {},
      origDevice: {}
    };
  },

  computed: {
    isTheSameDeviceCode() {
      return this.device.device_code === this.origDevice.device_code;
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('snackbar', ['setSnack']),

    refreshData() {
      WarehouseDevicesService.getById(this.id)
        .then(({ data }) => {
          this.device = {
            ...data,
            states: data.states.map(({ id }) => id)
          };
          this.origDevice = cloneDeep(this.device);
        })
        .catch(console.error);
    },

    cancel() {
      this.$emit('cancel');
    },

    update: onceRequest(function update() {
      if (!this.$refs.form.validate()) {
        return;
      }

      return this.checkWarhouseDeviceCode()
        .then(confirm => {
          return !confirm || this.showConfirm();
        })
        .then(confirm => {
          if (!confirm) {
            return;
          }
          if (!this.isTheSameDeviceCode) {
            return this.payAttentionConfirm().then(confirm => {
              if (confirm) {
                return this.updateDevice();
              }
            });
          } else {
            return this.updateDevice();
          }
        });
    }),

    payAttentionConfirm() {
      return this.$refs.confirm.open(
        null,
        `${this.$t('ITEM.PAY_ATTENTION')}: ${this.$t(
          'DEVICES.CONFIRM_UPD_MESSAGE'
        )}`,
        {
          messageClass: 'text-center text-md-h6 text-lg-h5 text-xl-h4',
          cancelBtnText: this.$t('BUTTON.CANCEL'),
          agreeBtnText: this.$t('BUTTON.UPDATE'),
          agreeBtnColor: 'success'
        }
      );
    },

    updateDevice() {
      return WarehouseDevicesService.update(this.id, this.device)
        .then(({ data }) => {
          this.setSnack({
            status: 'success',
            text: this.$t('MESSAGE.SUCCESS')
          });

          this.$emit('created', data.data);
        })
        .catch(this.handleErrors);
    },

    removeDevice() {
      WarehouseDevicesService.remove(this.id)
        .then(({ data }) => {
          this.setSnack({
            status: 'success',
            text: this.$t('MESSAGE.SUCCESS')
          });

          this.$emit('updated');
        })
        .catch(this.handleErrors);
    },

    deleteDevice() {
      this.$refs.confirm
        .open(
          null,
          `${this.$t('ITEM.PAY_ATTENTION')}: ${this.$t(
            'DEVICES.CONFIRM_UPD_MESSAGE'
          )}`,
          {
            messageClass: 'text-center text-md-h6 text-lg-h5 text-xl-h4',
            cancelBtnText: this.$t('BUTTON.CANCEL'),
            agreeBtnText: this.$t('BUTTON.DELETE'),
            agreeBtnColor: 'error'
          }
        )
        .then(confirm => {
          if (confirm) {
            this.removeDevice();
          }
        });
    }
  }
};
</script>
